import type { AvatarUploadResponse, SafeUser, UserListQuery } from "@playarena/shared";
import type { ApiResponse } from "@playarena/shared";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getEnv } from "@/utils/helpers";

import { setUser } from "../slices/authSlice";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${getEnv()?.VITE_API_URL}/${getEnv()?.VITE_APP_VERSION}/user`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["User", "UserList", "UserStats"],
  endpoints: builder => ({
    getCurrentUser: builder.query<ApiResponse<SafeUser>, void>({
      query: () => "/profile",
      providesTags: (_result, _error) => [{ type: "User" }],
    }),

    getProfile: builder.query<ApiResponse<SafeUser>, string>({
      query: id => "/profile" + (id ? `/${id}` : ""),
      providesTags: (result, error, id) => [{ type: "User", id }],
    }),

    updateProfile: builder.mutation<ApiResponse<SafeUser>, Partial<SafeUser["profile"]>>({
      query: data => ({
        url: "/update/profile",
        method: "PUT",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update user profile in auth state if needed
            dispatch({ type: "user/setProfile", payload: data.data });
          }
        } catch (error) {
          console.error("userApi.updateProfile error:", error);
        }
      },
      invalidatesTags: ["User"],
    }),

    changePassword: builder.mutation<
      ApiResponse<{ message: string }>,
      { currentPassword: string; newPassword: string }
    >({
      query: data => ({
        url: "/change-password",
        method: "POST",
        body: data,
      }),
    }),

    uploadAvatar: builder.mutation<ApiResponse<AvatarUploadResponse>, File>({
      query: file => {
        const formData = new FormData();
        formData.append("avatar", file);

        return {
          url: "/upload-avatar",
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: async (file, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update user avatar in auth state
            dispatch(setUser({ ...data.data.user }));
          }
        } catch (error) {
          console.error("Avatar upload failed:", error);
        }
      },
      invalidatesTags: ["User"],
    }),

    removeAvatar: builder.mutation<ApiResponse<{ user: SafeUser }>, void>({
      query: () => ({
        url: "/remove-avatar",
        method: "DELETE",
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Remove avatar from auth state
            dispatch(setUser({ ...data.data.user }));
          }
        } catch (error) {
          console.error("Avatar removal failed:", error);
        }
      },
      invalidatesTags: ["User"],
    }),

    // Admin routes - User management
    getUserList: builder.query<ApiResponse<SafeUser[]>, UserListQuery>({
      query: params => {
        const searchParams = new URLSearchParams();

        if (params.page) searchParams.append("page", params.page.toString());
        if (params.limit) searchParams.append("limit", params.limit.toString());
        if (params.role) searchParams.append("role", params.role);
        if (params.country) searchParams.append("country", params.country);
        if (params.emailVerified)
          searchParams.append("emailVerified", params.emailVerified.toString());
        if (params.isActive) searchParams.append("isActive", params.isActive.toString());
        if (params.search) searchParams.append("search", params.search);
        if (params.sortBy) searchParams.append("sortBy", params.sortBy);
        if (params.sortOrder) searchParams.append("sortOrder", params.sortOrder);
        if (params.dateFrom) searchParams.append("dateFrom", params.dateFrom);
        if (params.dateTo) searchParams.append("dateTo", params.dateTo);

        const queryString = searchParams.toString();
        return `/list${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["UserList"],
    }),

    // Super admin routes
    promoteUser: builder.mutation<
      ApiResponse<{ message: string; user: SafeUser }>,
      { id: string; role?: string }
    >({
      query: ({ id, ...data }) => ({
        url: `/promote/${id}`,
        method: "PUT",
        body: data,
      }),
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update user role in local state
            dispatch({ type: "user/updateUserRole", payload: { id, user: data.data.user } });
          }
        } catch (error) {
          console.error("userApi.promoteUser error:", error);
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: "User", id }, "UserList", "UserStats"],
    }),

    deleteUser: builder.mutation<ApiResponse<{ message: string }>, string>({
      query: id => ({
        url: `/delete/${id}`,
        method: "DELETE",
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            // Remove user from local state
            dispatch({ type: "user/removeUser", payload: id });
          }
        } catch (error) {
          console.error("userApi.deleteUser error:", error);
        }
      },
      invalidatesTags: (result, error, id) => [{ type: "User", id }, "UserList", "UserStats"],
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useUploadAvatarMutation,
  useRemoveAvatarMutation,
  useGetUserListQuery,
  usePromoteUserMutation,
  useDeleteUserMutation,
} = userApi;
